import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-liquid";
const TITLE = "Shopify Liquid: Templates anpassen statt Apps stapeln";
const DESCRIPTION =
  "Shopify Liquid verstehen und nutzen: Sections, Snippets und Metafelder im Theme anpassen, statt für jede Kleinigkeit eine App zu installieren. Mit Praxisbeispielen und Kostenrahmen.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Liquid ist die Template-Sprache, mit der Shopify-Themes gebaut sind. Wer sie beherrscht, löst die meisten Anpassungswünsche direkt im Theme: eigene Sections, bedingte Anzeigen, Metafelder-Ausgabe, Bundle-Logik auf der Produktseite. Das ist günstiger, schneller und besser für die Ladezeit als der übliche Weg über Apps, die für jede Kleinigkeit eigene Skripte nachladen.";

const faqs = [
  {
    q: "Was ist Shopify Liquid?",
    a: "Liquid ist die Template-Sprache von Shopify. Jedes Theme besteht aus Liquid-Dateien, die Produktdaten, Collections und Shop-Einstellungen in HTML verwandeln. Anpassungen daran geschehen direkt im Theme-Code.",
  },
  {
    q: "Was lässt sich mit Liquid anpassen?",
    a: "Praktisch alles Sichtbare: eigene Sections für den Editor, bedingte Inhalte je nach Produkt oder Kunde, Ausgabe von Metafeldern, Bundle-Anzeigen, Trust-Elemente und individuelle Produktseiten-Logik.",
  },
  {
    q: "Wann reicht Liquid, wann brauche ich eine App?",
    a: "Alles, was nur die Darstellung betrifft, gehört ins Theme. Eine App oder Custom App brauchen Sie, wenn Logik außerhalb der Seite läuft: Schnittstellen, automatisierte Prozesse, eigene Admin-Oberflächen.",
  },
  {
    q: "Machen Theme-Anpassungen den Shop langsamer?",
    a: "Sauber geschriebenes Liquid wird auf Shopifys Servern gerendert und belastet die Ladezeit kaum. Langsam machen Shops vor allem Apps, die eigene JavaScript-Bündel in jede Seite laden.",
  },
  {
    q: "Was kosten Liquid-Anpassungen?",
    a: "Kleinere Anpassungen liegen häufig bei wenigen Stunden. Ich rechne mit 110 € netto pro Stunde ab und nenne nach Sichtung einen festen Rahmen, bevor ich anfange.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-liquid")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Entwicklung",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Entwicklung"
      title="Shopify Liquid: was im Theme geht, braucht keine App"
      intro="Viele Shops bezahlen monatlich für Apps, die eine Stunde Theme-Arbeit ersetzen würden. Dieser Überblick zeigt, was mit Liquid direkt im Theme lösbar ist und wo die Grenze zu echter Entwicklung liegt."
      source="ratgeber-shopify-liquid"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Sections, Snippets, Metafelder",
          p: [
            "Moderne Shopify-Themes bestehen aus Sections, die Sie im Editor anordnen, und Snippets für wiederkehrende Bausteine. Eigene Sections machen Inhalte pflegbar, ohne dass jede Änderung ein Eingriff in den Code bleibt.",
            "Metafelder ergänzen Produkte und Collections um eigene Daten, etwa Material, Pflegehinweise oder technische Tabellen. Liquid gibt sie an der richtigen Stelle aus, ganz ohne Zusatz-App.",
          ],
        },
        {
          h: "Typische Wünsche, die Liquid direkt löst",
          p: [
            "Aus der Praxis wiederholen sich dieselben Anfragen. Fast alle lassen sich im Theme umsetzen, statt eine weitere App zu installieren.",
          ],
          list: [
            "Bundle-Anzeige und Staffelhinweise auf der Produktseite",
            "Bedingte Inhalte je nach Tag, Kollektion oder Kundengruppe",
            "Lieferzeit-, Verfügbarkeits- und Trust-Hinweise an definierten Stellen",
            "Eigene Sections für Vergleichstabellen, FAQ oder Zusatzinformationen",
            "Ausgabe und Filterung von Metafeldern auf Kategorieseiten",
          ],
        },
        {
          h: "Warum weniger Apps mehr bringen",
          p: [
            "Jede App lädt eigene Skripte und Styles, häufig auf jeder Seite des Shops. Zehn kleine Apps summieren sich schnell zu einer spürbaren Bremse und zu monatlichen Kosten, die den einmaligen Entwicklungsaufwand übersteigen.",
            "Die Rechnung ist simpel: Eine App für 20 € im Monat kostet in zwei Jahren 480 € und bleibt im Betrieb. Die gleiche Funktion im Theme kostet einmalig wenige Stunden und gehört danach Ihnen.",
          ],
        },
        {
          h: "Wo die Grenze zur App liegt",
          p: [
            "Liquid endet dort, wo Prozesse beginnen: Datenabgleich mit Warenwirtschaft, automatisierte Abläufe nach Bestellung, eigene Oberflächen im Admin. Das ist Aufgabe einer Custom App, nicht des Themes.",
            "Eine ehrliche Einordnung vorab spart doppelt: Sie installieren keine überflüssige App und beauftragen keine App-Entwicklung für etwas, das das Theme schon kann.",
          ],
        },
        {
          h: "Ablauf und Kosten",
          p: [
            "Sie beschreiben das gewünschte Ergebnis, ich sichte das Theme und nenne einen festen Aufwandsrahmen. Umsetzung zu 110 € netto pro Stunde, kleine Anpassungen liegen häufig bei zwei bis fünf Stunden.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung beauftragen" },
        { to: "/shopify-app-entwicklung", label: "Wann eine App nötig ist" },
        { to: "/ratgeber/shopify-metafields", label: "Metafelder richtig nutzen" },
        { to: "/blog/shopify-apps-auswaehlen", label: "App oder Theme-Anpassung" },
        { to: "/kontakt", label: "Anpassung anfragen" },
      ]}
    />
  );
}
